<template>
  <div>
    <v-card
      class="custom-table"
      :class="{ 'show-select': enableSelectToggle, 'hide-empty': hideEmptyResult, 'table-is-loading': isLoading }"
    >
      <v-toolbar flat class="custom-table-toolbar" height="auto" :class="{ 'no-wrap': noWrapToolbar }">
        <v-toolbar-items class="toolbar-left-items flex-grow-1">
          <!-- Table Name -->
          <div v-if="$validate.DataValid(tableName)" class="custom-table-title">
            <span class="mr-4">{{ $t(tableName) }}</span>
            <small v-if="$validate.DataValid(tableSmallText)">{{ $t(tableSmallText) }}</small>
          </div>

          <!-- Search Bar -->
          <Searchbar
            v-if="enableSearch"
            :searchText.sync="searchText"
            :searchPlaceholder="searchPlaceholder"
            :searchType="searchType"
            :searchTypeOptions="searchTypeOptions"
            @search-clicked="$emit('search-clicked', $event)"
            @search-input="$emit('search-input', $event)"
            @search-type-changed="$emit('update:searchType', $event)"
            :enableMultiTypeSearch="enableMultiTypeSearch"
            enableClear
            :isSearching="isLoading"
          />

          <!-- 進階搜尋 Button -->
          <FormButton
            v-if="enableAdvancedSearch"
            text="table.advanceSearch"
            class="mr-3 mt-2 mb-3"
            @click="_clickAdvancedSearch()"
            :class="{ 'pointer-loading': isLoading }"
          />

          <!-- Search Date Range -->
          <FormDatePicker
            v-if="enableDateRange"
            isDateRange
            :dateValue.sync="searchDateRange"
            hideDetails
            customInputClass="mr-3 mt-2 mb-3 search-input-field"
            :placeholder="dateRangePlaceholder"
            @changed="_selectDateRange()"
            :readonly="isLoading"
          />

          <!-- Search Single Date -->
          <FormDatePicker
            v-if="enableSearchDate"
            :dateValue.sync="searchDate"
            hideDetails
            customInputClass="mr-3 mt-2 mb-3 search-input-field"
            :placeholder="datePlaceholder"
            @changed="_selectDate()"
            :isYearMonthOnly="searchDateYearMonth"
            :disableClear="disableSingleDateClear"
            :readonly="isLoading"
          />

          <slot name="append-toolbar-left"></slot>
        </v-toolbar-items>

        <v-spacer></v-spacer>

        <v-toolbar-items class="toolbar-right-items">
          <slot name="append-toolbar-right"></slot>

          <!--------------- Default Button --------------->
          <!-- clear search -->
          <v-btn
            v-if="enableClearAll"
            text
            small
            color="error"
            :class="{ 'pointer-loading': isLoading }"
            @click="_clickClearAll()"
          >
            <v-icon class="mr-1 fi fi-rr-cross-small" color="error" size="18"></v-icon>
            {{ $t('table.clearSearch') }}
          </v-btn>

          <!-- refresh -->
          <v-btn
            v-if="enableRefresh"
            text
            small
            color="primary"
            :class="{ 'pointer-loading': isLoading }"
            @click="_clickRefresh()"
          >
            <v-icon class="mr-1" color="primary" size="18"> {{ icon.mdiRefresh }} </v-icon>
            {{ $t('table.reloadTable') }}
          </v-btn>

          <!-- export data -->
          <v-btn
            v-if="enableExport"
            text
            small
            color="#215003"
            :class="{ 'pointer-loading': isLoading }"
            @click="_clickExport()"
          >
            <v-progress-circular v-if="exporting" class="mr-1" indeterminate color="#215003" :size="15" width="3" />
            <v-icon class="mr-1" v-else color="#215003" size="18"> {{ icon.mdiFileTableOutline }} </v-icon>
            {{ $t('table.exportReport') }}
          </v-btn>

          <!-- import data -->
          <v-btn
            v-if="enableImport"
            text
            small
            color="#215003"
            target="_blank"
            rel="noreferrer noopenner"
            @click="_triggerUploadFile()"
          >
            <v-icon class="mr-1" color="#215003" size="18"> {{ icon.mdiTableArrowLeft }} </v-icon>
            {{ $t('table.importData') }}
          </v-btn>
          <v-file-input
            v-show="false"
            id="table-import-file"
            accept=".csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            @change="_clickImport($event)"
          ></v-file-input>
        </v-toolbar-items>
      </v-toolbar>

      <div
        v-if="enableConditionFilter"
        class="custom-table-filter px-4 d-flex align-center"
        :class="{ 'pointer-loading': isLoading }"
      >
        <v-menu
          v-model="conditionFilterOpen"
          transition="scale-transition"
          offset-y
          min-width="auto"
          :close-on-content-click="false"
          :disabled="isLoading"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon type="button" small v-bind="attrs" v-on="on" class="my-2">
              <v-icon class="fi fi-rr-filter" size="18"></v-icon>
            </v-btn>
            <v-btn
              v-show="selectedConditions.length < 1"
              class="px-1 my-2"
              type="button"
              v-bind="selectedConditions.length < 1 ? attrs : {}"
              v-on="selectedConditions.length < 1 ? on : {}"
              elevation="0"
            >
              {{ $t('table.addFilter') }}
            </v-btn>
          </template>

          <v-list>
            <v-list-item
              link
              v-for="(item, index) in conditionFilterOptions"
              :key="index"
              :disabled="item.disabled"
              @click.prevent="_onConditionSelected(item.value)"
            >
              <v-list-item-title class="text-body-2">{{ item.text }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        <v-chip-group>
          <v-chip
            v-for="(value, index) in selectedConditions"
            :key="value"
            outlined
            text-color="colorGrey"
            close
            :close-icon="icon.mdiClose"
            @click:close="_removeCondition(index)"
          >
            {{ _getConditionName(value) }}
          </v-chip>
        </v-chip-group>
      </div>

      <slot name="append-tool"></slot>

      <v-data-table
        class="custom-table"
        v-model="selectedRow"
        :locale="locale"
        :loading="isLoading"
        :headers="formatTableHeaders"
        :items="tableData"
        :items-per-page="hideFooter ? -1 : pageLimit"
        :page="page"
        :server-items-length="hideFooter ? tableData.length : itemTotal"
        :options.sync="options"
        :height="isFixedHeader ? 'calc(3.125rem * ' + (pageLimit + 1.1) + ')' : ''"
        :fixed-header="isFixedHeader"
        :show-select="enableSelectToggle"
        :show-expand="enableExpand"
        :show-group-by="showHeaderGroupButton"
        :group-by="groupBy"
        :single-select="isSingleSelect"
        :single-expand="isSingleExpand"
        :hide-default-header="disableHeader"
        :hide-default-footer="hideFooter"
        :disable-filtering="!enableFiltering"
        :disable-pagination="hideFooter"
        :disable-sort="!enableSort"
        :multi-sort="isMultiSort"
        :must-sort="isMustSort"
        :sort-by="defaultSortKey"
        :sort-desc="defaultSortDesc"
        :footer-props="{
          showCurrentPage: true,
          showFirstLastPage: $vuetify.breakpoint.xsOnly ? false : !disableFirstLastPage,
          disablePagination: hideFooter,
          itemsPerPageText: $t('table.itemPerPage'),
          itemsPerPageOptions: pageLimitOptions,
          itemsPerPageAllText: $t('table.all'),
          pageText: $t('table.displayItems', {
            first: (page - 1) * pageLimit + 1,
            last: Math.min(page * pageLimit, itemTotal === -1 ? tableData.length : itemTotal),
          }),
        }"
        :loading-text="$t('table.processing')"
        :no-data-text="$t(tableEmptyText)"
        :no-results-text="$t(tableEmptyText)"
        :dense="isDense"
        :mobile-breakpoint="breakpoint"
        @input="_updateSelectedRow"
      >
        <!-- Header checkboxes with action -->
        <template v-slot:header.data-table-select="{ props, on }">
          <div class="d-flex align-center">
            <v-simple-checkbox
              v-on="on"
              v-ripple
              v-bind="props"
              @click="_onCustomSelectClick($event, -1)"
              :disabled="disabledSelectAll"
              :class="{
                'pointer-none': processing || disabledSelectAll || tempDisableSelect,
                'checkbox-disabled': disabledSelectAll,
                'checkbox-hidden': disabledSelectAll,
              }"
            ></v-simple-checkbox>
            <v-select
              v-if="enableSelectAction"
              v-model="selectAction"
              class="action-selector"
              background-color="transparent"
              :items="selectActionOptionList"
              :label="$t('table.action')"
              solo
              flat
              height="24"
              color="white"
              dense
              hide-details
              @change="_selectActionChanged($event)"
              :disabled="!$validate.DataValid(tableData)"
            >
              <template v-slot:append>
                <v-icon class="fi fi-rr-angle-small-down" size="18"></v-icon>
              </template>
            </v-select>
          </div>
        </template>

        <!-- Items checkboxes -->
        <template v-slot:item.data-table-select="{ isSelected, on, attrs, item, select }">
          <div>
            <v-simple-checkbox
              v-on="on"
              v-bind="attrs"
              v-ripple
              :value="isSelected"
              @input="select($event)"
              @click="_onCustomSelectClick($event, item.id)"
              :disabled="item.disabled"
              :readonly="item.readonly"
              :class="{
                'pointer-none': processing || item.disabled || item.readonly || tempDisableSelect,
                'checkbox-readonly': item.readonly,
                'checkbox-disabled': item.disabled,
              }"
            ></v-simple-checkbox>
          </div>
        </template>

        <!-- Table Loading -->
        <template v-slot:loading>
          <v-skeleton-loader type="table-tbody"></v-skeleton-loader>
        </template>

        <!-- Group Row Header -->
        <template v-slot:group.header="{ group }">
          <td colspan="8" class="text-start">
            {{ $t(`tableGroup.${group}`) }}
          </td>
        </template>

        <!-- Table Body (draggable) -->
        <template v-if="enableDrag" v-slot:body="{ isMobile, items, headers }">
          <tbody v-if="isLoading && tableData.length < 1">
            <tr class="v-data-table__empty-wrapper">
              <td colspan="4">
                <v-skeleton-loader type="table-tbody"></v-skeleton-loader>
              </td>
            </tr>
          </tbody>
          <tbody v-else-if="!isLoading && tableData.length < 1">
            <tr class="v-data-table__empty-wrapper">
              <td colspan="4">{{ $t(tableEmptyText) }}</td>
            </tr>
          </tbody>
          <template v-else-if="tableData.length > 0">
            <draggable v-model="dragList" tag="tbody" :disabled="processing" @change="_onListDrag()">
              <tr v-for="item in items">
                <template v-for="headItem in headers">
                  <TableCell :isMobile="isMobile" :headItem="headItem" :hideTitle="headItem.value === 'actionDrag'">
                    <slot :name="`item.${headItem.value}`" :item="item">
                      <v-icon
                        v-if="headItem.value === 'actionDrag'"
                        size="24"
                        color="colorBlack"
                        style="cursor: grab;"
                        >{{ icon.mdiDragHorizontalVariant }}</v-icon
                      >
                      <div v-else-if="headItem.value === 'actionViewAndDelete'" class="d-flex align-center justify-end">
                        <v-btn
                          depressed
                          text
                          color="primary"
                          :ripple="false"
                          class="pa-0 mr-4"
                          small
                          :to="{ name: actionViewRouteLink, params: { id: item.id } }"
                        >
                          <v-icon class="fi fi-rr-eye mr-1" size="18"></v-icon>
                          {{ $t('table.view') }}
                        </v-btn>
                        <v-btn
                          small
                          depressed
                          text
                          color="error"
                          :ripple="false"
                          class="pa-0"
                          @click="_clickActionDelete(item.id)"
                        >
                          <v-icon class="fi fi-rr-trash mr-1" size="18"></v-icon>
                          {{ $t('delete') }}
                        </v-btn>
                      </div>
                      <div v-else-if="headItem.value === 'actionDelete'" class="d-flex align-center justify-end">
                        <v-btn
                          small
                          depressed
                          text
                          color="error"
                          :ripple="false"
                          class="pa-0"
                          @click="_clickActionDelete(item.id)"
                        >
                          <v-icon class="fi fi-rr-trash mr-1" size="18"></v-icon>
                          {{ $t('delete') }}
                        </v-btn>
                      </div>
                      <div
                        v-else-if="headItem.value === 'minActionEditAndDelete'"
                        class="d-flex align-center justify-end"
                      >
                        <v-btn
                          text
                          icon
                          min-width="max-content"
                          class="pa-0 mx-2"
                          :ripple="false"
                          @click="_clickActionEdit(item.id)"
                          :class="{ 'pointer-none': processing }"
                        >
                          <v-icon class="fi fi-ss-pen-circle" color="accent" size="24"></v-icon>
                        </v-btn>
                        <v-btn
                          text
                          icon
                          min-width="max-content"
                          class="pa-0 mx-2"
                          :ripple="false"
                          @click="_clickActionDelete(item.id)"
                          :class="{ 'pointer-none': processing }"
                        >
                          <v-icon class="fi fi-ss-cross-circle" color="red" size="24"></v-icon>
                        </v-btn>
                      </div>
                      <div v-else-if="headItem.value === 'minActionDelete'" class="d-flex align-center justify-end">
                        <v-btn
                          text
                          icon
                          min-width="max-content"
                          class="pa-0 mx-2"
                          :ripple="false"
                          @click="_clickActionDelete(item.id)"
                          :class="{ 'pointer-none': processing }"
                        >
                          <v-icon class="fi fi-ss-cross-circle" color="red" size="24"></v-icon>
                        </v-btn>
                      </div>
                      <span v-else-if="item[headItem.value]">{{ item[headItem.value] }}</span>
                    </slot>
                  </TableCell>
                </template>
              </tr>
            </draggable>
          </template>
        </template>

        <!-- pass through scoped slots -->
        <template v-for="(_, scopedSlotName) in $scopedSlots" v-slot:[scopedSlotName]="slotData">
          <slot :name="scopedSlotName" v-bind="slotData" />
        </template>

        <!-- pass through normal slots -->
        <template v-for="(_, slotName) in $slots" v-slot:[slotName]>
          <slot :name="slotName" />
        </template>

        <!------------------- 近期帳單 ------------------->
        <template v-slot:[`item.order_type`]="{ item }">
          <span v-if="typeof item.order_type === 'string'">{{ item.order_type | formatOrderType }}</span>
          <template v-else-if="Array.isArray(item.order_type)">
            <span v-for="(type, index) in item.order_type" :key="`order_type_${type}`" class="d-inline"
              >{{ type | formatOrderType }}{{ index !== item.order_type.length - 1 ? ' & ' : '' }}</span
            >
          </template>
        </template>

        <template v-slot:[`item.order_items`]="{ item }">
          <span v-if="typeof item.order_items === 'string'">{{ item.order_items }}</span>
          <div
            v-else-if="Array.isArray(item.order_items)"
            v-for="(oItem, i) in item.order_items"
            :key="`order-item-${i}`"
            class="my-1"
          >
            {{ oItem.name }}
            <small class="d-block" v-if="$validate.DataValid(oItem.details)" style="font-size: 10px;">{{
              oItem.details
            }}</small>
          </div>
        </template>

        <!------------------- Date related ------------------->
        <!-- DD/MM(W) -->
        <template v-slot:[`item.dates`]="{ item }">
          <span v-for="(date, i) in item.dates" :key="`course-date-${i}_${date}`">
            {{ date.substring(8, 10) }}/{{ date.substring(5, 7) }} ({{
              $d($formatter.convertDate(date), 'weekdayShort')
            }}){{ i !== item.dates.length - 1 ? ',' : '' }}
          </span>
        </template>

        <!-- DD/MM/YYYY -->
        <template v-slot:[`item.full_date`]="{ item, header }">
          <span v-if="$validate.DataValid(item.date)">
            {{ item.date.substring(8, 10) }}/{{ item.date.substring(5, 7) }}/{{ item.date.substring(0, 4) }}
            {{ header.hasWeekday ? `(${$d($formatter.convertDate(item.date), 'weekdayShort')})` : '' }}
          </span>
        </template>

        <!-- DD-MM -->
        <template v-slot:[`item.date_month`]="{ item }">
          <span v-if="$validate.DataValid(item.date)">
            {{ item.date.substring(8, 10) }}-{{ item.date.substring(5, 7) }}
          </span>
        </template>

        <!-- W -->
        <template v-slot:[`item.weekday`]="{ item }">
          <span v-if="$validate.DataValid(item.date)">{{ $d($formatter.convertDate(item.date), 'weekdayShort') }}</span>
        </template>

        <!-- MM -->
        <template v-slot:[`item.month`]="{ item, header }">
          <span v-if="$validate.DataValid(item.month)">
            {{ header.hasYear && item.year ? `${item.year}/` : '' }}{{ $formatter.pad(item.month) }}
          </span>
        </template>

        <!------------------- Common ------------------->
        <template v-slot:[`item.id`]="{ item }">
          <span>{{ $formatter.generateId(item.id) }}</span>
        </template>

        <template v-slot:[`item.grade`]="{ item }">
          <span>{{ gfGetGradeText(item.grade) }}</span>
        </template>

        <template v-slot:[`item.timeslot`]="{ item }">
          <span v-if="$validate.DataValid(item.start_time) && $validate.DataValid(item.end_time)">
            {{ item.start_time }} - {{ item.end_time }}
          </span>
        </template>

        <template v-slot:[`item.unit_price`]="{ item }">
          <div :class="{ 'text-decoration-line-through': $validate.DataValid(item.discount_price) }">
            HK${{ $formatter.separator(item.unit_price) }}
          </div>
          <div v-if="$validate.DataValid(item.discount_price)">HK${{ $formatter.separator(item.discount_price) }}</div>
        </template>

        <template v-slot:[`item.total`]="{ item }">
          <span v-if="$validate.DataValid(item.total)">HK${{ $formatter.separator(item.total) }}</span>
        </template>

        <template v-slot:[`item.cost`]="{ item }">
          <span v-if="$validate.DataValid(item.cost)">HK${{ $formatter.separator(item.cost) }}</span>
        </template>

        <!-- 人數 (n/total) -->
        <template v-slot:[`item.current_per_max_student_num`]="{ item, header }">
          <span v-if="header.hasUnit">{{
            $t('calendar.currentPerMaxStudent', {
              current: item.current_student_num || '0',
              max: item.max_student && item.max_student === -1 ? $t('noLimit') : item.max_student || '0',
            })
          }}</span>
          <span v-else
            >{{ item.current_student_num || '0' }}/{{
              item.max_student && item.max_student === -1 ? $t('noLimit') : item.max_student || '0'
            }}</span
          >
        </template>

        <!-- QTY setter -->
        <template v-slot:[`item.qtySetter`]="{ item }">
          <QtySetter
            :qty.sync="item.qty"
            :max="item.inventory"
            :min="item.qty_min || 1"
            @update-value="$emit('update:item.qty', $event)"
          ></QtySetter>
        </template>

        <template v-slot:[`item.minActionEdit`]="{ item }">
          <v-btn
            text
            icon
            min-width="max-content"
            class="pa-0 mx-2"
            :ripple="false"
            @click="_clickActionEdit(item.id)"
            :class="{ 'pointer-none': processing }"
          >
            <v-icon class="fi fi-ss-pen-circle" color="accent" size="24"></v-icon>
          </v-btn>
        </template>
        <template v-slot:[`item.minActionDelete`]="{ item }">
          <v-btn
            text
            icon
            min-width="max-content"
            class="pa-0 mx-2"
            :ripple="false"
            @click="_clickActionDelete(item.id)"
            :class="{ 'pointer-none': processing }"
          >
            <v-icon class="fi fi-ss-cross-circle" color="red" size="24"></v-icon>
          </v-btn>
        </template>
        <template v-slot:[`item.minActionEditAndDelete`]="{ item }">
          <div class="d-flex align-center justify-end">
            <v-btn
              text
              icon
              min-width="max-content"
              class="pa-0 mx-2"
              :ripple="false"
              @click="_clickActionEdit(item.id)"
              :class="{ 'pointer-none': processing }"
            >
              <v-icon class="fi fi-ss-pen-circle" color="accent" size="24"></v-icon>
            </v-btn>
            <v-btn
              text
              icon
              min-width="max-content"
              class="pa-0 mx-2"
              :ripple="false"
              @click="_clickActionDelete(item.id)"
              :class="{ 'pointer-none': processing }"
            >
              <v-icon class="fi fi-ss-cross-circle" color="red" size="24"></v-icon>
            </v-btn>
          </div>
        </template>

        <template v-slot:[`item.actionView`]="{ item }">
          <v-btn
            depressed
            text
            color="primary"
            :ripple="false"
            class="pa-0"
            small
            :to="{ name: actionViewRouteLink, params: { id: item.id } }"
          >
            <v-icon class="fi fi-rr-eye mr-1" size="18"></v-icon>
            {{ $t('table.view') }}
          </v-btn>
        </template>
        <template v-slot:[`item.actionDelete`]="{ item }">
          <v-btn small depressed text color="error" :ripple="false" class="pa-0" @click="_clickActionDelete(item.id)">
            <v-icon class="fi fi-rr-trash mr-1" size="18"></v-icon>
            {{ $t('delete') }}
          </v-btn>
        </template>
        <template v-slot:[`item.actionViewAndDelete`]="{ item }">
          <div class="d-flex align-center justify-end">
            <v-btn
              depressed
              text
              color="primary"
              :ripple="false"
              class="pa-0 mr-4"
              small
              :to="{ name: actionViewRouteLink, params: { id: item.id } }"
            >
              <v-icon class="fi fi-rr-eye mr-1" size="18"></v-icon>
              {{ $t('table.view') }}
            </v-btn>
            <v-btn small depressed text color="error" :ripple="false" class="pa-0" @click="_clickActionDelete(item.id)">
              <v-icon class="fi fi-rr-trash mr-1" size="18"></v-icon>
              {{ $t('delete') }}
            </v-btn>
          </div>
        </template>
        <slot></slot>
      </v-data-table>

      <slot name="append-widget"></slot>
    </v-card>
  </div>
</template>

<script>
import { mdiClose, mdiRefresh, mdiFileTableOutline, mdiTableArrowLeft, mdiDragHorizontalVariant } from '@mdi/js'

import QtySetter from '@/components/QtySetter.vue'
import FormDatePicker from '@/components/formField/FormDatePicker.vue'
import FormInput from '@/components/formField/FormInput.vue'
import Searchbar from '@/components/Searchbar.vue'

import draggable from 'vuedraggable'
import TableCell from './layout/TableCell.vue'

export default {
  // ------ page properties ------
  name: 'Datatable',
  components: {
    QtySetter,
    FormDatePicker,
    FormInput,
    Searchbar,
    draggable,
    TableCell,
  },
  props: {
    isLoading: {
      type: Boolean,
      default: false,
    },
    exporting: {
      type: Boolean,
      default: false,
    },
    tableData: {
      type: Array,
      default: () => [],
    },
    tableHeaders: {
      type: Array,
      default: () => [],
    },
    itemTotal: {
      type: Number,
      default: 0,
    },
    actionViewRouteLink: {
      type: String,
      default: '',
    },
    breakpoint: {
      type: Number,
      default: 960,
    },
    datePlaceholder: {
      type: String,
      default: 'table.searchDate',
    },
    dateRangePlaceholder: {
      type: String,
      default: 'table.searchDateRange',
    },
    defaultSortDesc: {
      type: Array,
      default: () => [true],
    },
    defaultSortKey: {
      type: Array,
      default: () => ['create_date'],
    },
    disableFirstLastPage: {
      type: Boolean,
      default: false,
    },
    disableHeader: {
      type: Boolean,
      default: false,
    },
    disablePagination: {
      type: Boolean,
      default: false,
    },
    enableSort: {
      type: Boolean,
      default: false,
    },
    enableAdvancedSearch: {
      type: Boolean,
      default: false,
    },
    enableClearAll: {
      type: Boolean,
      default: false,
    },
    enableDateRange: {
      type: Boolean,
      default: false,
    },
    enableExpand: {
      type: Boolean,
      default: false,
    },
    enableExport: {
      type: Boolean,
      default: false,
    },
    enableFiltering: {
      type: Boolean,
      default: false,
    },
    showHeaderGroupButton: {
      type: Boolean,
      default: false,
    },
    groupBy: {
      type: String | null,
      default: null,
    },
    enableRefresh: {
      type: Boolean,
      default: false,
    },
    enableSearch: {
      type: Boolean,
      default: false,
    },
    enableMultiTypeSearch: {
      type: Boolean,
      default: false,
    },
    enableSearchDate: {
      type: Boolean,
      default: false,
    },
    enableSelectToggle: {
      type: Boolean,
      default: false,
    },
    enableSelectAction: {
      type: Boolean,
      default: false,
    },
    selectActionOption: {
      type: Array,
      default: () => [],
    },
    initSearchKeyword: {
      type: String,
      default: '',
    },
    initSearchDate: {
      type: String,
      default: '',
    },
    isDense: {
      type: Boolean,
      default: false,
    },
    isFixedHeader: {
      type: Boolean,
      default: false,
    },
    isMultiSort: {
      type: Boolean,
      default: false,
    },
    isMustSort: {
      type: Boolean,
      default: true,
    },
    isSingleExpand: {
      type: Boolean,
      default: false,
    },
    isSingleSelect: {
      type: Boolean,
      default: false,
    },
    page: {
      type: Number,
      default: 1,
    },
    pageLimit: {
      type: Number,
      default: 10,
    },
    pageLimitOptions: {
      type: Array,
      default: () => [10, 20, 50],
    },
    searchPlaceholder: {
      type: String,
      default: 'search',
    },
    searchType: {
      type: String,
      default: '',
    },
    searchTypeOptions: {
      type: Array,
      default: () => [],
    },
    selectedTableRow: {
      type: Array,
      default: () => [],
    },
    tableEmptyText: {
      type: String,
      default: 'table.noData',
    },
    tableName: {
      type: String,
      default: '',
    },
    tableSmallText: {
      type: String,
      default: '',
    },
    noWrapToolbar: {
      type: Boolean,
      default: false,
    },
    disabledSelectAll: {
      type: Boolean,
      default: false,
    },
    tempDisableSelect: {
      type: Boolean,
      default: false,
    },
    enableImport: {
      type: Boolean,
      default: false,
    },
    searchDateYearMonth: {
      type: Boolean,
      default: false,
    },
    disableSingleDateClear: {
      type: Boolean,
      default: false,
    },
    enableConditionFilter: {
      type: Boolean,
      default: false,
    },
    conditionFilterOptions: {
      type: Array,
      default: () => [], // { value: '', text: '', siblings: [] }
    },
    selectedConditions: {
      type: Array,
      default: () => [],
    },
    hideEmptyResult: {
      type: Boolean,
      default: false,
    },
    enableDrag: {
      type: Boolean,
      default: false,
    },
    dispatchUpdateOnChange: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  // ------ page variables ------
  data: vm => ({
    icon: {
      mdiClose,
      mdiRefresh,
      mdiFileTableOutline,
      mdiTableArrowLeft,
      mdiDragHorizontalVariant,
    },

    options: {},
    searchDate: '',
    searchDateRange: [],
    searchText: '',
    tableInit: false,
    selectedRow: [],
    searchKeyInit: true,
    selectAction: '',

    conditionFilterOpen: false,

    dragList: [],

    selectActionOptionList: [
      { text: vm.$t('table.edit'), value: 'edit' },
      { text: vm.$t('delete'), value: 'delete' },
    ],
  }),

  // ------ mutation observer ------
  watch: {
    options: {
      handler() {
        this._updateOptions()
      },
      deep: true,
    },
    selectedTableRow: {
      handler(val) {
        this.selectedRow = val
      },
      deep: true,
      immediate: true,
    },
    pageLimit: {
      handler(val) {
        if (val !== this.options.itemsPerPage) {
          this.options.itemsPerPage = val
        }
      },
    },
    initSearchKeyword: {
      handler(val) {
        if (this.searchKeyInit) {
          this.searchText = val
        }

        this.searchKeyInit = false
      },
      immediate: true,
    },
    initSearchDate: {
      handler(val) {
        this.searchDate = val
      },
      immediate: true,
    },
    selectActionOption: {
      handler(val) {
        if (this.$validate.DataValid(val, true)) {
          this.selectActionOptionList = val
        }
      },
      deep: true,
      immediate: true,
    },
    tableData: {
      handler(val) {
        this.dragList = val
      },
      deep: true,
      immediate: true,
    },
  },

  // ------ computed values ------
  computed: {
    locale() {
      switch (this.$i18n.locale) {
        case 'en':
          return 'en'
        case 'cn':
          return 'zh-CN'
        default:
          return 'zh-HK'
      }
    },
    processing() {
      return this.isLoading || this.$store.getters.isLoading
    },
    hideFooter() {
      return this.disablePagination || this.enableDrag
    },
    formatTableHeaders() {
      let headers = [...this.tableHeaders]
      if (this.enableDrag && this.tableData.length > 0) {
        headers.unshift({ value: 'actionDrag', text: '', width: 50 })
      }
      return headers
    },
  },

  // ------ page functions ------
  methods: {
    _clickAdvancedSearch() {
      if (this.isLoading) return
      if (this.hideFooter) {
        this.$emit('advanced-search-clicked', false)
      } else {
        this.$emit('advanced-search-clicked', true)
      }
    },
    _clickClearAll() {
      if (this.isLoading) return
      this.searchText = ''
      this.searchDate = ''
      this.searchDateRange = []
      this.$emit('all-search-cleared')
    },
    _clickRefresh() {
      if (this.isLoading) return
      this.$emit('refresh-clicked')
    },
    _clickExport() {
      if (this.isLoading) return
      if (this.exporting) {
        this.$store.dispatch('toggleSnack', { message: 'message.exporting', type: 'error' })
        return
      }
      this.$emit('export-clicked')
    },
    _triggerUploadFile() {
      document.getElementById('table-import-file').click()
    },
    _clickImport(file) {
      this.$emit('import-clicked', file)
    },
    _clickActionEdit(id) {
      this.$emit('edit-clicked', id)
    },
    _clickActionDelete(id) {
      this.$emit('delete-clicked', id)
    },
    _updateOptions() {
      if (this.$store.getters.isLoading) {
        this.$store.dispatch('toggleSnack', { message: 'processing' })
        return
      }

      if (this.tableInit) {
        if (this.options.itemsPerPage !== this.pageLimit) {
          this.$emit('update:page', 1) // reset to first page if view options changed
        } else {
          this.$emit('update:page', this.options.page)
        }
        this.$emit('update:pageLimit', this.options.itemsPerPage)
        this.$emit('options-update', this.options)
      }

      this.tableInit = true
    },
    _updateSelectedRow(rows) {
      this.$emit('update:selectedTableRow', rows)
      this.$emit('selected-row-changed', rows)
    },
    _selectDate() {
      this.$emit('date-selected', this.searchDate)
    },
    _selectDateRange() {
      this.$emit('daterange-selected', this.searchDateRange)
    },
    _onCustomSelectClick(ev, id) {
      if (this.processing) {
        ev.preventDefault()
        return
      }

      this.$emit('checkbox-click', id)
    },
    _selectActionChanged(val) {
      this.$emit('select-action-changes', val)
      this.$nextTick(() => {
        this.selectAction = ''
      })
    },

    _onListDrag() {
      this.$emit('update:tableData', this.dragList)
      this.$emit('ondrag', this.dragList)
      if (this.dispatchUpdateOnChange === true) {
        this.$store.dispatch('setDataIsUpdated', true)
      }
    },

    _onConditionSelected(value) {
      if (this.isLoading) return
      const list = JSON.parse(JSON.stringify(this.selectedConditions))
      if (!list.includes(value)) {
        const found = this.conditionFilterOptions.find(el => el.value === value)
        if (found) {
          if (this.$validate.DataValid(found.siblings)) {
            let siblingIndex = -1
            for (let i = 0; i < list.length; i++) {
              if (found.siblings.includes(list[i])) {
                siblingIndex = i
                break
              }
            }

            if (siblingIndex !== -1) {
              list.splice(siblingIndex, 1)
            }
          }

          list.push(value)
          this.$emit('update:selectedConditions', list)
          this.$emit('condition-changed', list)
        }
      }
    },
    _getConditionName(value) {
      const found = this.conditionFilterOptions.find(el => el.value === value)
      return found ? found.text : value
    },
    _removeCondition(index) {
      if (this.isLoading) return

      const list = JSON.parse(JSON.stringify(this.selectedConditions))
      list.splice(index, 1)
      this.$emit('update:selectedConditions', list)
      this.$emit('condition-changed', list)
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep .v-data-table.custom-table .v-data-table-header-mobile .v-text-field .v-select__selections {
  margin-top: 0.5rem;
}
::v-deep .custom-table-toolbar .v-input--selection-controls .v-input__slot > .v-label {
  font-size: 0.875rem;
}

::v-deep .custom-table-toolbar .v-input input,
::v-deep .custom-table-toolbar .v-input label {
  font-size: 0.825rem;
}

::v-deep .v-data-table.custom-table > .v-data-table__wrapper > table > tbody > tr > th,
::v-deep .v-data-table.custom-table > .v-data-table__wrapper > table > thead > tr > th,
::v-deep .v-data-table.custom-table > .v-data-table__wrapper > table > tfoot > tr > th {
  font-size: 0.75rem;
  font-weight: 500;
}

::v-deep .v-data-table > .v-data-table__wrapper > table > tbody > tr > td,
::v-deep .v-data-table > .v-data-table__wrapper > table > thead > tr > td,
::v-deep .v-data-table > .v-data-table__wrapper > table > tfoot > tr > td {
  padding-top: 10px;
  padding-bottom: 10px;
  color: var(--v-colorBlack-base);
  font-size: 0.875rem;

  &.v-data-table__mobile-row {
    padding-top: 5px;
    padding-bottom: 5px;
  }

  & .v-btn.v-size--default {
    font-size: 0.875rem;
  }
}
::v-deep .v-data-table > .v-data-table__wrapper > table > tbody > tr > td {
  white-space: pre-line;
}

::v-deep .v-data-table.custom-table > .v-data-table__wrapper .v-data-table__mobile-table-row {
  padding-top: 1rem;
}
::v-deep .v-data-table.custom-table > .v-data-table__wrapper .v-data-table__mobile-row:last-of-type {
  padding-bottom: 1rem;
}
::v-deep .v-data-table.custom-table > .v-data-table__wrapper .v-data-table__mobile-row {
  min-height: 2.2rem;
}

::v-deep .v-data-table.custom-table .v-data-table__empty-wrapper {
  & > td {
    height: 175px !important;
    color: var(--v-colorBlack-base) !important;
  }
}

::v-deep .custom-table.hide-empty:not(.table-is-loading) .v-data-table__empty-wrapper {
  display: none !important;
}

::v-deep .v-data-table.custom-table.v-data-table--mobile tbody .v-data-table__empty-wrapper {
  display: block;
  & > td {
    width: 100% !important;
    display: flex !important;
    justify-content: center;
    align-items: center;
  }
}

.hide-page-select .v-data-footer__select {
  visibility: none;
  opacity: 0;
  pointer-events: none;
}

.v-dialog.table-dialog {
  margin: 0 auto;
}

.red-text {
  color: var(--v-error-base);
}

::v-deep .v-data-table-header .v-simple-checkbox {
  line-height: 1 !important;
  & .v-icon {
    color: var(--v-colorWhite-base) !important;
  }
}

::v-deep .v-data-table-header-mobile__select {
  min-width: unset !important;
  & .v-simple-checkbox {
    line-height: 1 !important;
    & .v-icon {
      color: var(--v-primary-base) !important;
    }
  }
}

::v-deep.custom-table.show-select {
  & .v-data-table-header-mobile {
    position: relative;
    height: 50px;
  }
  & .v-data-table-header-mobile > tr > th {
    height: 0;
  }
  & .v-data-table-header-mobile__wrapper {
    position: absolute;
    top: 0;
    right: 0;
    padding: 12px 16px 0;
  }
}

::v-deep tbody .v-simple-checkbox {
  & .v-icon {
    color: var(--v-primary-base) !important;
  }
}

::v-deep .v-data-table-header th > span,
::v-deep .v-data-table__mobile-row__header {
  white-space: nowrap !important;
}

::v-deep .action-selector {
  width: 70px;
  max-width: 70px;
  min-width: 70px;
  margin-bottom: 0 !important;

  & .v-icon {
    color: #ffffff;
  }

  & .v-input__control {
    min-height: 24px !important;
    border: 1px solid #ffffff;
  }

  & .v-input__slot {
    padding: 0 5px !important;
  }

  & .v-label,
  & .v-select__selection {
    font-size: 12px;
    color: #ffffff !important;
  }

  & .v-input__append-inner {
    margin-top: 0 !important;
  }
}

::v-deep .search-input-selector {
  width: 80px;
  max-width: 80px;
  min-width: 80px;
  margin-bottom: 0 !important;

  & input {
    display: none !important;
  }

  & .v-input__icon {
    color: #d9d9d9;
    height: 10px !important;
    width: 10px !important;
    min-width: 10px !important;
  }

  & .v-input__control {
    min-height: 24px !important;
    // border: 1px solid #d9d9d9;
    border: none;
  }

  & .v-input__slot {
    padding: 0 5px !important;
  }

  & .v-label,
  & .v-select__selection {
    font-size: 12px;
    color: var(--v-colorGrey-base) !important;
    margin-right: 0 !important;
    width: 100%;
    // text-align: center;
  }

  & .v-input__append-inner {
    // display: none !important;
    margin-top: 0 !important;
  }
}

::v-deep .search-input-field .v-input__prepend-inner {
  margin-top: 6px !important;
}

::v-deep .v-data-table-header-mobile__select .action-selector {
  & .v-icon,
  & .v-label,
  & .v-select__selection {
    color: var(--v-primary-base) !important;
  }

  & .v-input__control {
    border-color: var(--v-primary-base) !important;
  }
}

::v-deep .v-data-table-header .action-selector {
  &.v-input {
    &.v-input--is-disabled {
      & .v-input__slot {
        background: transparent !important;
      }

      & .v-input__slot fieldset {
        border-color: var(--v-primary-base) !important;
      }
    }
  }
}

::v-deep .v-row-group__header {
  &:hover {
    background: #eeeeee !important;
  }

  & td {
    border-bottom: none !important;
    font-size: 0.75rem !important;
    font-weight: 500 !important;
    height: 2.5rem !important;
  }
}
</style>
